/* eslint-disable react/no-unknown-property */
/**
 * Fighter3DAnimatedCard.jsx — WebGL 3D animated fighter with move triggers
 * (Feb 2026 owner directive — Roc is the first-target hero for the pipeline).
 *
 * Pipeline:
 *   1. Owner drops `fighter.animated_model.model_url` (GLB/FBX with skeleton).
 *   2. Owner also drops `fighter.animated_model.animation_map` — a dict that
 *      maps Roc's canonical move IDs (primal_burst, feral_flip, etc.) to the
 *      GLB's actual animation clip names ("Punch", "Uppercut", ...).
 *   3. This component loads the model, wires the animation mixer via drei's
 *      `useAnimations`, and exposes a move-button rail so the user can trigger
 *      any move → the mixer plays the mapped clip on top of the idle loop.
 *
 * Retarget-friendly:
 *   Mixamo humanoid rigs (Roc, Cid, Solana, David, Bram, Nathaniel) all share
 *   the same skeleton naming convention. Once Roc works, any Mixamo humanoid
 *   drops in with an animation_map swap — no other code changes needed.
 *
 * Demo mode:
 *   If no model_url is set, the component offers a "Load Demo Soldier" button
 *   that loads three.js's public Soldier.glb (with built-in Idle/Walk/Run
 *   clips) so you can prove the pipeline works before dropping Roc's real GLB.
 */
import { Suspense, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  Environment,
  useGLTF,
  useAnimations,
  ContactShadows,
} from "@react-three/drei";
import * as THREE from "three";

// Public demo model — three.js sample soldier w/ Idle · Walk · Run baked in.
const DEMO_MODEL_URL = "https://threejs.org/examples/models/gltf/Soldier.glb";

// Same accent hex map as Fighter3DCard.
const ACCENT_HEX = {
  amber: "#F59E0B", emerald: "#10B981", violet: "#8B5CF6", rose: "#F43F5E",
  cyan: "#06B6D4", fuchsia: "#D946EF", orange: "#F97316", slate: "#64748B",
  sky: "#0EA5E9", yellow: "#EAB308", lime: "#84CC16", red: "#EF4444",
  purple: "#A855F7", indigo: "#6366F1", teal: "#14B8A6", gray: "#9CA3AF",
  blue: "#3B82F6", pink: "#EC4899",
};

/**
 * <AnimatedRig> — loads a GLB + wires drei's useAnimations mixer.
 * Exposes a play(clipName) function via a ref-forwarded control prop.
 */
function AnimatedRig({ modelUrl, colorHex, activeClip, onAnimReady }) {
  const group = useRef();
  const { scene, animations } = useGLTF(modelUrl);
  const { actions, names } = useAnimations(animations, group);

  // Tint the model with the hero's accent as a subtle rim emissive.
  useEffect(() => {
    scene.traverse((obj) => {
      if (obj.isMesh && obj.material) {
        obj.castShadow = true;
        // Clone so the tint doesn't bleed across heroes if the same URL is reused
        if (!obj.userData._tinted) {
          obj.material = obj.material.clone();
          obj.material.emissive = new THREE.Color(colorHex);
          obj.material.emissiveIntensity = 0.08;
          obj.userData._tinted = true;
        }
      }
    });
    onAnimReady && onAnimReady(names);
  }, [scene, colorHex, names, onAnimReady]);

  // Play the active clip. Fade smoothly between clips.
  useEffect(() => {
    if (!actions || Object.keys(actions).length === 0) return;
    const target = activeClip && actions[activeClip] ? actions[activeClip] : (actions.Idle || Object.values(actions)[0]);
    if (!target) return;
    Object.values(actions).forEach((a) => {
      if (a && a !== target) a.fadeOut(0.25);
    });
    target.reset().fadeIn(0.25).play();
    return () => {
      target.fadeOut(0.25);
    };
  }, [actions, activeClip]);

  return <primitive ref={group} object={scene} position={[0, -1.3, 0]} scale={1.35} />;
}

/**
 * <FighterAnimatedScene> — R3F scene wrapper.
 */
function FighterAnimatedScene({ modelUrl, colorHex, activeClip, onAnimReady }) {
  return (
    <Canvas
      camera={{ position: [0, 1.4, 5.2], fov: 42 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      shadows
    >
      <ambientLight intensity={0.4} />
      <directionalLight position={[3, 5, 4]} intensity={1.0} castShadow shadow-mapSize-width={1024} shadow-mapSize-height={1024} />
      <pointLight position={[-3, 2, -3]} intensity={0.5} color={colorHex} />

      <Suspense fallback={null}>
        <Environment preset="warehouse" />
      </Suspense>

      <Suspense fallback={null}>
        <AnimatedRig modelUrl={modelUrl} colorHex={colorHex} activeClip={activeClip} onAnimReady={onAnimReady} />
      </Suspense>

      <ContactShadows position={[0, -1.35, 0]} opacity={0.55} scale={5.5} blur={2.5} far={2.2} />

      <OrbitControls
        enablePan={false}
        enableZoom={false}
        minPolarAngle={Math.PI / 3.5}
        maxPolarAngle={Math.PI / 1.9}
      />
    </Canvas>
  );
}

/**
 * <Fighter3DAnimatedCard> — main export.
 *
 * Reads `fighter.animated_model = { model_url, animation_map }`.
 * If not set, offers a demo-load button.
 */
export default function Fighter3DAnimatedCard({ fighter, height = 460 }) {
  const colorHex = ACCENT_HEX[fighter.accent] || "#A855F7";
  const cfg = fighter.animated_model || null;

  // Model URL: from config, or demo when user opts in.
  const [demoLoaded, setDemoLoaded] = useState(false);
  const modelUrl = cfg?.model_url || (demoLoaded ? DEMO_MODEL_URL : null);

  // Move → clip mapping. When we're on demo mode, we use whatever clips ship with Soldier.glb.
  const animMap = cfg?.animation_map || {
    idle: "Idle",
    walk: "Walk",
    run: "Run",
  };

  const [activeClip, setActiveClip] = useState(animMap.idle || "Idle");
  const [availableNames, setAvailableNames] = useState([]);

  // If the loaded model doesn't have the currently-selected clip, fall back to the first available.
  useEffect(() => {
    if (availableNames.length > 0 && !availableNames.includes(activeClip)) {
      setActiveClip(availableNames[0]);
    }
  }, [availableNames, activeClip]);

  if (!modelUrl) {
    return (
      <div
        style={{ minHeight: height, width: "100%" }}
        className="rounded-xl overflow-hidden border-2 border-dashed border-neutral-700 bg-gradient-to-br from-black via-neutral-950 to-black p-6 flex flex-col items-center justify-center text-center"
        data-testid={`showdown-3d-anim-card-${fighter.id}`}
      >
        <div className="text-4xl mb-3">{fighter.icon || "🥋"}</div>
        <div className="text-xs uppercase tracking-widest text-yellow-300 mb-1">🟡 Animation Model Pending</div>
        <div className="text-sm text-white font-bold mb-2">{fighter.name} · 3D animation slot</div>
        <div className="text-[11px] text-neutral-400 max-w-md leading-relaxed mb-4">
          Drop a rigged Mixamo-style GLB URL into <code className="text-neutral-300">fighter.animated_model.model_url</code> and map each move ID to a clip name in <code className="text-neutral-300">animation_map</code>. The pipeline auto-loads and wires the animation mixer.
        </div>
        <button
          onClick={() => setDemoLoaded(true)}
          data-testid={`showdown-3d-anim-demo-${fighter.id}`}
          className="text-[10px] uppercase tracking-widest px-3 py-2 rounded border border-yellow-500/60 bg-yellow-500/10 text-yellow-300 font-bold hover:brightness-125 transition"
        >
          🎬 Load Demo Soldier (prove pipeline works)
        </button>
      </div>
    );
  }

  // Move buttons — driven from the animation_map keys (each maps to a clip name).
  const moveButtons = Object.entries(animMap).filter(([, clipName]) => availableNames.length === 0 || availableNames.includes(clipName));

  return (
    <div
      style={{ width: "100%" }}
      className="rounded-xl overflow-hidden border-2 border-neutral-800 bg-gradient-to-br from-black via-neutral-950 to-black"
      data-testid={`showdown-3d-anim-card-${fighter.id}`}
    >
      <div style={{ height, position: "relative" }}>
        <div className="absolute top-2 left-2 z-10 text-2xl leading-none pointer-events-none"
             style={{ textShadow: "0 0 8px #000, 0 2px 4px #000", color: colorHex }}>
          {fighter.icon || "★"}
        </div>
        <div className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-widest font-bold text-white pointer-events-none"
             style={{ textShadow: "0 1px 3px #000" }}>
          {fighter.name}
        </div>
        <FighterAnimatedScene
          modelUrl={modelUrl}
          colorHex={colorHex}
          activeClip={activeClip}
          onAnimReady={setAvailableNames}
        />
      </div>

      {/* Move rail */}
      <div className="p-3 border-t border-neutral-800 bg-black/60">
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
          Move Rail · Currently: <span className="text-white font-bold">{activeClip}</span>
          {demoLoaded && (
            <span className="ml-2 text-yellow-400 italic normal-case tracking-normal">· DEMO MODEL (Soldier.glb)</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {moveButtons.map(([moveId, clipName]) => (
            <button
              key={moveId}
              onClick={() => setActiveClip(clipName)}
              data-testid={`showdown-3d-move-${fighter.id}-${moveId}`}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border font-bold transition
                ${activeClip === clipName
                  ? "border-white bg-white/10 text-white"
                  : "border-neutral-700 bg-black/40 text-neutral-300 hover:brightness-125"}`}
              title={`Play "${clipName}" clip`}
            >
              {moveId.replace(/_/g, " ")}
            </button>
          ))}
          {availableNames.length > 0 && moveButtons.length === 0 && (
            <div className="text-[10px] italic text-yellow-400">
              None of the mapped clip names ({Object.values(animMap).join(", ")}) exist in this model. Available: {availableNames.join(", ")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Preload the demo model in the background so the "Load Demo" button feels snappy.
useGLTF.preload(DEMO_MODEL_URL);
